import { z } from 'zod';
import { validateDateRange, validateDateTimeRange, validateTimeRange } from '@/lib/validation';

export const REQUIRED_FIELDS_MESSAGE = 'Please complete all required fields.';

export const FIELD_MESSAGES = {
  email: 'Enter a valid email address.',
  phone: 'Enter a valid phone number.',
  organizationId: 'Enter a valid Organization ID.',
  headcount: 'Headcount must be at least 1.',
  hours: 'Hours must be greater than 0.',
  rate: 'Rate cannot be negative.',
  quantity: 'Quantity must be greater than 0.',
  startDate: 'Start date cannot be after end date.',
  endDate: 'End date cannot be before start date.',
  startTime: 'Time In and Time Out cannot be the same.',
  endTime: 'Time In and Time Out cannot be the same.',
  date: 'Enter a valid date.',
  amountDecimals: 'Amount cannot have more than 2 decimal places.',
  password: 'Password must be at least 6 characters.',
  passwordMatch: 'Passwords do not match.',
  remarksRequired: 'Enter at least 2 characters explaining the rejection.',
};

export function requiredText(label: string, max = 500, message?: string): z.ZodString {
  return z
    .string({ required_error: `${label} is required.`, invalid_type_error: `${label} is required.` })
    .trim()
    .min(1, message ?? `${label} is required.`)
    .max(max, `${label} cannot exceed ${max} characters.`);
}

export function optionalText(max = 500): z.ZodString {
  return z.string().trim().max(max, `Value cannot exceed ${max} characters.`);
}

export function nameRule(label = 'Name', max = 200, min = 1) {
  const base = z
    .string({ required_error: `${label} is required.`, invalid_type_error: `${label} is required.` })
    .trim()
    .min(min, min > 1 ? `${label} must be at least ${min} characters.` : `${label} is required.`)
    .max(max, `${label} cannot exceed ${max} characters.`);
  return base.refine((v) => /[A-Za-z0-9]/.test(v), `${label} must contain meaningful characters.`);
}

export const emailRule: z.ZodString = z
  .string({ invalid_type_error: FIELD_MESSAGES.email })
  .trim()
  .toLowerCase()
  .min(1, 'Email is required.')
  .email(FIELD_MESSAGES.email);

export const phoneRule: z.ZodString = z
  .string({ invalid_type_error: FIELD_MESSAGES.phone })
  .trim()
  .regex(/^\+?[0-9][0-9\s\-()]{6,19}$/, FIELD_MESSAGES.phone);

export const optionalPhoneRule = z.union([z.literal(''), phoneRule]);

export const organizationCodeRule = requiredText('Organization ID', 40, FIELD_MESSAGES.organizationId)
  .transform((v) => v.toUpperCase())
  .pipe(z.string().regex(/^[A-Z0-9_]+$/, FIELD_MESSAGES.organizationId));

export const loginPasswordRule: z.ZodString = z.string().min(1, 'Password is required.');

export const newPasswordRule: z.ZodString = z
  .string({ required_error: 'New password is required.' })
  .min(6, FIELD_MESSAGES.password)
  .max(200, 'Password cannot exceed 200 characters.');

export function confirmPasswordRule(getPassword: () => string) {
  return z
    .string({ required_error: 'Confirm your password.' })
    .min(1, 'Confirm your password.')
    .refine((v) => v === getPassword(), FIELD_MESSAGES.passwordMatch);
}

/** Coerces numeric input. Empty string becomes NaN (never silently zero). */
function strictNumber(message: string): z.ZodEffects<z.ZodNumber, number, unknown> {
  return z.preprocess(
    (value) => {
      if (value === null || value === undefined) return value;
      if (typeof value === 'string' && value.trim() === '') return NaN;
      const n = typeof value === 'number' ? value : Number(value);
      return Number.isFinite(n) ? n : NaN;
    },
    z.number({ invalid_type_error: message, required_error: message })
  );
}

const maxTwoDecimals = (value: number) => Math.abs(value * 100 - Math.round(value * 100)) < 1e-9;

export const headcountRule = strictNumber(FIELD_MESSAGES.headcount)
  .refine((v) => Number.isInteger(v), 'Headcount must be a whole number.')
  .refine((v) => v > 0, FIELD_MESSAGES.headcount);

export const hoursRule = strictNumber(FIELD_MESSAGES.hours).refine((v) => v > 0, FIELD_MESSAGES.hours);

export const rateRule = strictNumber(FIELD_MESSAGES.rate)
  .refine((v) => v >= 0, FIELD_MESSAGES.rate)
  .refine(maxTwoDecimals, FIELD_MESSAGES.amountDecimals);

export const quantityRule = strictNumber(FIELD_MESSAGES.quantity)
  .refine((v) => v >= 0, FIELD_MESSAGES.quantity)
  .refine(maxTwoDecimals, FIELD_MESSAGES.amountDecimals);

export const moneyRule = strictNumber('Enter a valid amount.')
  .refine((v) => v >= 0, 'Amount cannot be negative.')
  .refine(maxTwoDecimals, FIELD_MESSAGES.amountDecimals);

export const positiveIntegerRule = (message: string) =>
  strictNumber(message).refine((v) => Number.isInteger(v), message).refine((v) => v > 0, message);

export function positiveNumberRule(message: string) {
  return strictNumber(message).refine((v) => v > 0, message).refine(maxTwoDecimals, FIELD_MESSAGES.amountDecimals);
}

export function dateFieldRule(label: string): z.ZodString {
  return requiredText(label, 10).regex(/^\d{4}-\d{2}-\d{2}$/, `${label} must be a valid date.`);
}

export function dateRangeSuperRefine(startKey: string, endKey: string) {
  return (data: Record<string, unknown>, ctx: z.RefinementCtx): void => {
    const start = data[startKey] as string | undefined;
    const end = data[endKey] as string | undefined;
    if (!start || !end) return;
    const result = validateDateRange(start, end);
    if (!result.valid) {
      if (result.startError) ctx.addIssue({ code: z.ZodIssueCode.custom, path: [startKey], message: result.startError });
      if (result.endError) ctx.addIssue({ code: z.ZodIssueCode.custom, path: [endKey], message: result.endError });
    }
  };
}

export function sameDaySuperRefine(startKey: string, endKey: string) {
  return (data: Record<string, unknown>, ctx: z.RefinementCtx): void => {
    const start = data[startKey] as string | undefined;
    const end = data[endKey] as string | undefined;
    if (!start || !end) return;
    if (start !== end) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: [endKey], message: 'Start Date and End Date must be the same.' });
    }
  };
}

export function timeRangeSuperRefine(startKey: string, endKey: string) {
  return (data: Record<string, unknown>, ctx: z.RefinementCtx): void => {
    const start = data[startKey] as string | undefined;
    const end = data[endKey] as string | undefined;
    if (!start || !end) return;
    const result = validateTimeRange(start, end, { startLabel: 'Time In', endLabel: 'Time Out' });
    if (!result.valid) {
      if (result.startError) ctx.addIssue({ code: z.ZodIssueCode.custom, path: [startKey], message: result.startError });
      if (result.endError) ctx.addIssue({ code: z.ZodIssueCode.custom, path: [endKey], message: result.endError });
    }
  };
}

export function dateTimeRangeSuperRefine(startDateKey: string, startTimeKey: string, endDateKey: string, endTimeKey: string) {
  return (data: Record<string, unknown>, ctx: z.RefinementCtx): void => {
    const startDate = data[startDateKey] as string | undefined;
    const startTime = data[startTimeKey] as string | undefined;
    const endDate = data[endDateKey] as string | undefined;
    const endTime = data[endTimeKey] as string | undefined;
    if (!startDate || !endDate || !startTime || !endTime) return;
    const result = validateDateTimeRange(startDate, startTime, endDate, endTime);
    if (!result.valid) {
      if (result.startError) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: startTime ? [startDateKey] : [startDateKey], message: result.startError });
      }
      if (result.endError) ctx.addIssue({ code: z.ZodIssueCode.custom, path: [endDateKey], message: result.endError });
    }
  };
}

export function remarksRule(opts: { required?: boolean; min?: number; max?: number } = {}): z.ZodString {
  const { required = false, min = 2, max = 500 } = opts;
  const base = z.string().trim();
  if (required) {
    return base
      .min(min, FIELD_MESSAGES.remarksRequired)
      .max(max, `Remarks cannot exceed ${max} characters.`);
  }
  return base.max(max, `Remarks cannot exceed ${max} characters.`);
}
